// src/services/assetService.ts
import api from './api';

// Utility function to properly format asset data
export const normalizeAsset = (asset: any) => {
  // Ensure we have a valid asset object
  if (!asset || typeof asset !== 'object') {
    return {
      _id: `generated-${Date.now()}`,
      token: 'unknown',
      symbol: 'UNKNOWN',
      amount: 0,
      lastKnownPriceUsd: 0
    };
  }

  // Make a copy of the asset
  const normalizedAsset = { ...asset };

  // Fix _id if missing
  if (!normalizedAsset._id) {
    normalizedAsset._id = normalizedAsset.token || `asset-${Date.now()}`;
  }

  // Fix symbol if missing
  if (!normalizedAsset.symbol) {
    // Try to generate a symbol from the token address
    const tokenAddress = normalizedAsset.token || '';
    if (tokenAddress.length > 8) {
      // Use part of the token address as a symbol if it exists
      normalizedAsset.symbol = tokenAddress.substring(0, 6).toUpperCase();
    } else {
      normalizedAsset.symbol = 'UNKNOWN';
    }
  }

  // Fix amount if it's a string or invalid
  if (typeof normalizedAsset.amount !== 'number' || isNaN(normalizedAsset.amount)) {
    // Try to parse it as a number if it's a string
    if (typeof normalizedAsset.amount === 'string') {
      try {
        const parsedAmount = parseFloat(normalizedAsset.amount);
        normalizedAsset.amount = isNaN(parsedAmount) ? 0 : parsedAmount;
      } catch (e) {
        normalizedAsset.amount = 0;
      }
    } else {
      normalizedAsset.amount = 0;
    }
  }

  // Fix price if missing or invalid
  if (typeof normalizedAsset.lastKnownPriceUsd !== 'number' || isNaN(normalizedAsset.lastKnownPriceUsd)) {
    if (typeof normalizedAsset.lastKnownPriceUsd === 'string') {
      try {
        const parsedPrice = parseFloat(normalizedAsset.lastKnownPriceUsd);
        normalizedAsset.lastKnownPriceUsd = isNaN(parsedPrice) ? 0 : parsedPrice;
      } catch (e) {
        normalizedAsset.lastKnownPriceUsd = 0;
      }
    } else {
      normalizedAsset.lastKnownPriceUsd = 0;
    }
  }

  return normalizedAsset;
};

export const getUserAssets = async (privyUserId: string) => {
  try {
    const response = await api.get(`/assets/user/${privyUserId}`);
    
    // Normalize the assets data
    const assets = response.data;
    if (Array.isArray(assets)) {
      return assets.map(normalizeAsset);
    } else if (assets && typeof assets === 'object') {
      // Handle case where API returns an object instead of array
      const assetArray = Object.values(assets);
      return assetArray.map(normalizeAsset);
    } else {
      return [];
    }
  } catch (error) {
    console.error('Error fetching user assets:', error);
    return []; // Return empty array instead of throwing to prevent UI breakage
  }
};

export const createOrUpdateAsset = async (assetData: {
  privyUserId: string;
  token: string;
  symbol: string;
  amount: number;
  lastKnownPriceUsd: number;
}) => {
  try {
    // Normalize the asset data before sending
    const normalizedData = {
      ...assetData,
      amount: typeof assetData.amount === 'number' ? assetData.amount : parseFloat(String(assetData.amount)) || 0,
      lastKnownPriceUsd: typeof assetData.lastKnownPriceUsd === 'number' ? 
        assetData.lastKnownPriceUsd : parseFloat(String(assetData.lastKnownPriceUsd)) || 0
    };
    
    const response = await api.post('/assets', normalizedData);
    return normalizeAsset(response.data);
  } catch (error) {
    console.error('Error creating/updating asset:', error);
    throw error;
  }
};